import React, { useEffect, useMemo, useState } from 'react'
import { FormSelectField } from '@/components/Form/FormField'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { TradingAccountDto } from 'api/Clients/Types/TradingAccount'
import { NameDto } from 'api/Common/Types'
import ConfirmationModal from 'components/ConfirmationModal/ConfirmationModal'

import { SelectIBTradingAccount } from './SelectIBTradingAccount'
import { TradingAccountsPickerField } from './TradingAccountsPickerField'

interface Props {
  isOpen: boolean
  onClose(): void
  introducingBrokerId: string
  groups: NameDto<string>[]
  tradingAccounts?: NameDto<string>[]
  onSubmit: (tradingAccountIds: string[], groupId: string) => void | Promise<void>
}

interface RebateGroupData {
  tradingAccounts: NameDto<string>[]
  groupId?: string
}

const getInitialData = (tradingAccounts?: NameDto<string>[]): RebateGroupData => ({
  tradingAccounts: tradingAccounts ?? [],
  groupId: undefined,
})

const getTradingAccountsValue = (tradingAccounts: NameDto<string>[]) => {
  if (tradingAccounts.length === 0) return ''
  if (tradingAccounts.length <= 2) return tradingAccounts.map(({ name }) => name).join(', ')

  return `${tradingAccounts
    .slice(0, 2)
    .map(({ name }) => name)
    .join(', ')} +${tradingAccounts.length - 2} more`
}

export const AddRebateToGroupModal: React.FC<Props> = (props) => {
  const { introducingBrokerId, groups, tradingAccounts, onSubmit, isOpen, onClose } = props
  const [data, setData] = useState<RebateGroupData>(() => getInitialData(tradingAccounts))
  const [tradingAccountModal, setTradingAccountModal] = useState(false)
  const [confirmModal, setConfirmModal] = useState(false)

  useEffect(() => {
    if (!isOpen) return

    setData(getInitialData(tradingAccounts))
    setTradingAccountModal(false)
    setConfirmModal(false)
  }, [isOpen, tradingAccounts])

  const isValid = useMemo(
    () => Boolean(data.groupId) && data.tradingAccounts.length > 0,
    [data.groupId, data.tradingAccounts.length]
  )

  const onSelectTradingAccount = (tradingAccount: TradingAccountDto, checked?: boolean) => {
    setData((current) => ({
      ...current,
      tradingAccounts: checked
        ? [...current.tradingAccounts, { name: tradingAccount.name, id: tradingAccount.id }]
        : current.tradingAccounts.filter(({ id }) => id !== tradingAccount.id),
    }))
  }

  const handleSubmit = async () => {
    if (!data.tradingAccounts.length) return
    if (!data.groupId) return

    setConfirmModal(false)
    await onSubmit(
      data.tradingAccounts.map(({ id }) => id),
      data.groupId
    )
  }

  return (
    <>
      <ConfirmationModal
        contentText='Are you sure you want to add TA to the group?'
        yesButtonText='Yes'
        cancelButtonText='No'
        onYes={handleSubmit}
        onCancel={() => setConfirmModal(false)}
        visible={confirmModal}
      />

      <Dialog open={tradingAccountModal} onOpenChange={setTradingAccountModal}>
        <SelectIBTradingAccount
          onClose={() => setTradingAccountModal(false)}
          isMultiSelect
          introducingBrokerId={introducingBrokerId}
          initialValue={data.tradingAccounts.map(({ id }) => id)}
          onChange={onSelectTradingAccount}
        />
      </Dialog>

      <Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Add TA To Existing Group</DialogTitle>
          </DialogHeader>

          <TradingAccountsPickerField
            name='tradingAccounts'
            label='TA #'
            value={getTradingAccountsValue(data.tradingAccounts)}
            placeholder='Select trading account(s)'
            onSelect={() => setTradingAccountModal(true)}
          />
          <FormSelectField
            name='groupId'
            label='Group name'
            options={groups}
            value={data.groupId}
            showSearch
            onChange={(value) =>
              setData((current) => ({
                ...current,
                groupId: String(value),
              }))
            }
          />

          <DialogFooter>
            <Button variant='outline' onClick={onClose}>
              Cancel
            </Button>
            <Button
              onClick={() => setConfirmModal(true)}
              disabled={!isValid}
              data-test='add-rebate-button'
            >
              Save
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  )
}
